import { useCallback, useEffect, useRef, useState } from 'react';
import { useTRPCClientAPI } from '../../../../trpc/trpc';
import {
  SEND_TRACK_FALLBACK_URL,
  SongsBrewStatus,
  useSongsBrewState,
} from '../../../music24/artistHome/songsBrew/useSongsBrewState';
import { useWriteSongsBrewStatus } from '../../../music24/artistHome/songsBrew/useWriteSongsBrewStatus';
import type { SettingsSaveState } from '../SettingsSaveStatus';

export interface SongsBrewSetting {
  /** Null: no Newsletter group (an error, an old server, the platform is not set up). */
  status: SongsBrewStatus | null;
  isLoading: boolean;
  sendTrackUrl: string;
  /** A subscribe answered `unsubscribed`: the line "Subscribe again with the link in any issue". */
  needsIssueLink: boolean;
  toggle: (on: boolean) => void;
}

/**
 * The SongsBrew switch of Settings → Notifications (design: songsbrew-promotion c2-c7, c11). It reads
 * `newsletter.getState({ fresh: true })`. The switch moves at once; the answer sets the final state:
 * `subscribed` on, `refused` off and disabled, `unsubscribed` after a subscribe off with the issue
 * link line. A thrown call puts the switch back and shows the shared save error. A second tap waits
 * for the answer of the first one. The local status follows the query only when a new answer comes;
 * each call answer goes into the cache too, so Home and the welcome read it.
 */
export function useSongsBrewSetting(setSaveState: (state: SettingsSaveState) => void): SongsBrewSetting {
  const trpcClient = useTRPCClientAPI();
  const writeStatus = useWriteSongsBrewStatus();
  const { state, isLoading } = useSongsBrewState({ fresh: true });
  const [status, setStatus] = useState<SongsBrewStatus | null>(null);
  const [needsIssueLink, setNeedsIssueLink] = useState(false);
  const isPending = useRef(false);

  useEffect(() => {
    if (isPending.current) {
      return;
    }
    // `state` keeps its identity until a new answer comes, so a re-render never undoes an answer.
    setStatus(state?.status ?? null);
  }, [state]);

  const toggle = useCallback(
    (on: boolean) => {
      if (isPending.current || status === null || status === 'refused') {
        return;
      }
      const previous = status;
      isPending.current = true;
      setStatus(on ? 'subscribed' : 'unsubscribed');
      setNeedsIssueLink(false);
      setSaveState('saving');
      const call = on
        ? trpcClient.newsletter.subscribe.mutate({ source: 'music24-settings' })
        : trpcClient.newsletter.unsubscribe.mutate();
      call
        .then((answer) => {
          isPending.current = false;
          setStatus(answer.status);
          setNeedsIssueLink(on && answer.status === 'unsubscribed');
          setSaveState('saved');
          writeStatus(answer.status);
        })
        .catch((error: unknown) => {
          console.error(error);
          isPending.current = false;
          setStatus(previous);
          setSaveState('failed');
        });
    },
    [setSaveState, status, trpcClient, writeStatus]
  );

  return { status, isLoading, sendTrackUrl: state?.sendTrackUrl ?? SEND_TRACK_FALLBACK_URL, needsIssueLink, toggle };
}
